import Footer from "../../SharedModule/components/Footer";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  ROUTE_TIMESHEETS_REPORTS,
  TIME_VIEWER_AND_ADMIN_ACCESS,
} from "../../SharedModule/utils/constants";
import { Link } from "react-router";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  addElementToArray,
  getCostCenterChildrenIds,
} from "../utils/timesUtils";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useWhoIsOutReport } from "../hooks/useWhoIsOutReport";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { BiLeftArrowAlt, BiInfoCircle } from "react-icons/bi";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { Select } from "../../SharedModule/components/Select";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { Radio } from "../../SharedModule/components/Radio";
import { WhoIsOutUser } from "../types/times-module-types";
import {
  resetFilters,
  SelectCostCenterWhoIsOut,
  SelectCostCenterWhoIsOutComplete,
  SelectFilters,
  SelectIsInfoWhoIsOutLoaded,
  SelectProjectsWhoIsOut,
  setCostCenterWhoIsOutComplete,
  setFilters,
  setInfoWhoIsOut,
} from "../redux/reducers/whoIsOutReport.reducer";
import {
  nameDayCompleteDateFormat,
  quantityFormat,
  decodeURIComponentSafe,
} from "../../SharedModule/utils/formatters";
import { newUuid } from "../../SharedModule/utils/uuid";
import { getDateFromDateString } from "../../SharedModule/utils/dateUtils";
import {
  compare,
  groupDataByDate,
  groupDataByPerson,
  timeFrameOptios,
} from "../utils/sortUtils";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { TimesheetsService } from "../services/timesheets";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";

export const WhoIsOutReportsPage = () => {
  const dispatch = useDispatch();

  const filters = useSelector(SelectFilters);
  const isInfoWhoIsOutLoaded = useSelector(SelectIsInfoWhoIsOutLoaded);
  const costCenterWhoIsOut = useSelector(SelectCostCenterWhoIsOut);
  const costCenterWhoIsOutComplete = useSelector(
    SelectCostCenterWhoIsOutComplete
  );
  const projectsWhoIsOut = useSelector(SelectProjectsWhoIsOut);

  // order cc by id
  let costCenterToShow: any = [];
  if (costCenterWhoIsOut) {
    costCenterToShow = [...costCenterWhoIsOut].sort((a: any, b: any) => {
      return a.value < b.value ? -1 : 1;
    });
  }

  // ASUITE11-2699
  const [projectsWhoIsOutFiltered, setProjectsWhoIsOutFiltered] = useState(
    projectsWhoIsOut ?? []
  );

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);

  // Local info to set filters at first load
  const [localInfoWhoIsOut, setLocalInfoWhoIsOut] = useState({
    costCenterWhoIsOut: costCenterWhoIsOut,
    projectsWhoIsOut: projectsWhoIsOut,
  });

  const { isLoading, whoIsOutUserList } = useWhoIsOutReport(
    filters,
    isInfoWhoIsOutLoaded,
    userRolesList
  );

  // call endpoint to have page filters
  const fetchInfoWhoIsOut = async () => {
    let whoIsOutFilters,
      costCenterWhoIsOutData = [];
    await TimesheetsService.getWhoisOutFilters()
      .then((response) => {
        whoIsOutFilters = response.data;
      })
      .catch((error) => {
        // to check when remove roles that allows access to the page;
        console.error("Error fetching who is out filters: ", error);
      });
    if (whoIsOutFilters) {
      addElementToArray(costCenterWhoIsOutData, whoIsOutFilters.costCenters);
      dispatch(setCostCenterWhoIsOutComplete(whoIsOutFilters.costCenters));
    }
    return {
      costCenterWhoIsOut: costCenterWhoIsOutData || null,
      projectsWhoIsOut:
        whoIsOutFilters?.projects?.map((elem: any) => ({
          value: elem.entityId,
          label: elem.entityId + " - " + elem.entityName,
          categoryId: elem.categoryId,
          costCenterId: elem.costCenterId,
        })) || null,
    };
  };

  // ASUITE11-2479: To sort columns gruped by
  const [sortField, setSortField] = useState(
    filters.groupByDate ? "person" : "date"
  );
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState({
    orderBy: filters.groupByDate ? "person" : "date",
    criteria: "asc",
  });

  const groupedData = filters.groupByDate
    ? groupDataByDate(whoIsOutUserList)
    : groupDataByPerson(whoIsOutUserList);

  const handleSort = (field, sortOrderByParam) => {
    let order = sortOrderByParam;
    if (!order) {
      order = sortOrder === "asc" ? "desc" : "asc";
    }
    setSortBy({
      orderBy: field,
      criteria: order,
    });
    setSortField(field);
    setSortOrder(order);
  };

  const sortedData = Object.entries(groupedData).map(
    ([groupedValue, records]: [string, any]) => {
      const sortedRecords = [...records].sort((a, b) => {
        if (sortField === "date") {
          return sortOrder === "asc"
            ? compare(
                getDateFromDateString(a.date) > getDateFromDateString(b.date)
              )
            : compare(
                getDateFromDateString(b.date) > getDateFromDateString(a.date)
              );
        } else if (sortField === "hours") {
          return sortOrder === "asc" ? a.hours - b.hours : b.hours - a.hours;
        } else if (sortField === "person") {
          return sortOrder === "asc"
            ? compare(a.person.toLowerCase() > b.person.toLowerCase())
            : compare(b.person.toLowerCase() > a.person.toLowerCase());
        } else if (sortField === "description") {
          return sortOrder === "asc"
            ? compare(a.description > b.description)
            : compare(b.description > a.description);
        } else if (sortField === "costCenter") {
          return sortOrder === "asc"
            ? a.costCenter.number - b.costCenter.number
            : b.costCenter.number - a.costCenter.number;
        }
        return 0;
      });
      return { groupedValue, records: sortedRecords };
    }
  );

  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);

  const handleChange = (value: any, field: string) => {
    setSubmitEnabled(true);

    // ASUITE11-2699
    if (field === "costCenter") {
      if (value.length > 0) {
        let costCenterIds: any = [];
        value.forEach((element) => {
          costCenterIds = costCenterIds.concat(
            getCostCenterChildrenIds(
              costCenterWhoIsOutComplete,
              element.departmentId,
              "departmentId"
            )
          );
        });
        setProjectsWhoIsOutFiltered(
          projectsWhoIsOut.filter((project) =>
            costCenterIds.includes(project.costCenterId)
          )
        );
      } else {
        setProjectsWhoIsOutFiltered(projectsWhoIsOut);
      }
      setReportFilters({
        ...reportFilters,
        [field]: value,
        project: [],
      });
    } else {
      setReportFilters({
        ...reportFilters,
        [field]: value,
      });
    }
  };

  const setLocalFilters = (structureData) => {
    setReportFilters({
      timeFrame: filters.timeFrame,
      costCenter:
        structureData.costCenterWhoIsOut?.length === 1
          ? [structureData.costCenterWhoIsOut[0]]
          : filters.costCenter,
      project:
        structureData.projectsWhoIsOut?.length === 1
          ? [structureData.projectsWhoIsOut[0]]
          : filters.project,
      groupByDate: filters.groupByDate,
    });
  };

  const [reportFilters, setReportFilters] = useState({
    timeFrame: filters.timeFrame,
    costCenter: filters.costCenter,
    project: filters.project,
    groupByDate: filters.groupByDate,
  });

  useEffect(() => {
    document.title = "The Hub - Who Is Out In The Future Report";

    const loadInfoWhoIsOut = async () => {
      let metaInfoWhoIsOut: any = await fetchInfoWhoIsOut();
      setLocalInfoWhoIsOut(metaInfoWhoIsOut);
      dispatch(setInfoWhoIsOut(metaInfoWhoIsOut));
      setProjectsWhoIsOutFiltered(metaInfoWhoIsOut.projectsWhoIsOut);
      setLocalFilters(metaInfoWhoIsOut);
    };

    if (!isInfoWhoIsOutLoaded) {
      loadInfoWhoIsOut();
    } else {
      setLocalFilters(localInfoWhoIsOut);
    }

    dispatch(setActiveTab(ROUTE_TIMESHEETS_REPORTS));

    return () => {
      dispatch(resetFilters());
    };
  }, []);

  const setPageFilters = () => {
    if (reportFilters.groupByDate) {
      handleSort("person", "asc");
    } else {
      handleSort("date", "asc");
    }
    dispatch(setFilters(reportFilters));
  };

  return (
    <>
      {(!isInfoWhoIsOutLoaded || !rolesLoaded) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}
      {isInfoWhoIsOutLoaded &&
        rolesLoaded &&
        !verifyModuleAccess(
          true,
          TIME_VIEWER_AND_ADMIN_ACCESS,
          userRolesList
        ) && <ForbiddenPage />}
      {isInfoWhoIsOutLoaded &&
        rolesLoaded &&
        verifyModuleAccess(
          true,
          TIME_VIEWER_AND_ADMIN_ACCESS,
          userRolesList
        ) && (
          <div className="content">
            <div className="mt-2 button-header">
              <Link
                className="back-button-container text-decoration-none"
                to={ROUTE_TIMESHEETS_REPORTS}
              >
                <BiLeftArrowAlt className="back-button-arrow" />
                <span className="back-button-text">Back to Time reports</span>
              </Link>
            </div>
            <div className="d-flex flex-wrap">
              <h2 className="flex-fill">Who is out in the future</h2>
            </div>

            <form className="card entry-hour-box-background p-4 mt-3 mb-5">
              <div className="d-flex mb-4 justify-content-between">
                <div className="flex-column col-sm-2">
                  <label htmlFor="who-is-out-time-frame" className="col-form-label">
                    Time Frame
                  </label>
                  <Select
                    inputId="who-is-out-time-frame"
                    placeholder="All"
                    options={timeFrameOptios}
                    value={reportFilters.timeFrame}
                    onChange={(timeFrame) =>
                      handleChange(timeFrame, "timeFrame")
                    }
                    isDisabled={isLoading}
                  />
                </div>
                <div className="flex-column col-sm-3">
                  <label htmlFor="who-is-out-cost-center" className="col-form-label">
                    Cost Center
                    <Tooltip
                      title={<b>List users that belong to this Cost Center</b>}
                      placement="right"
                      slots={{ transition: Zoom }}
                      arrow
                    >
                      <span>
                        <BiInfoCircle className="disc-icon ms-1" />
                      </span>
                    </Tooltip>
                  </label>
                  <Select
                    inputId="who-is-out-cost-center"
                    isMulti
                    placeholder="All Cost Centers"
                    options={costCenterToShow}
                    value={reportFilters.costCenter}
                    onChange={(costCenter) =>
                      handleChange(costCenter, "costCenter")
                    }
                    isDisabled={isLoading}
                  />
                </div>
                <div className="flex-column col-sm-2">
                  <label htmlFor="who-is-out-projects" className="col-form-label">
                    Projects
                  </label>
                  <Select
                    inputId="who-is-out-projects"
                    isMulti
                    placeholder="All"
                    options={projectsWhoIsOutFiltered}
                    value={reportFilters.project}
                    onChange={(project) => handleChange(project, "project")}
                    isDisabled={isLoading}
                  />
                </div>
                <div className="flex-column col-sm-2">
                  <div className="col-form-label">Group By</div>
                  <div>
                    <div className="w-50 d-inline-block">
                      <Radio
                        label="Date"
                        value={reportFilters.groupByDate}
                        checked={reportFilters.groupByDate}
                        onChange={() => handleChange(true, "groupByDate")}
                      />
                    </div>
                    <div className="w-50 d-inline-block">
                      <Radio
                        label="User"
                        value={!reportFilters.groupByDate}
                        checked={!reportFilters.groupByDate}
                        onChange={() => handleChange(false, "groupByDate")}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ marginTop: "35px" }}
                >
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      setPageFilters();
                    }}
                    disabled={!submitEnabled}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>

            <hr className="header-separator" />

            {isLoading && (
              <Spinner
                style={{
                  marginLeft: "45%",
                  marginTop: "10%",
                }}
              />
            )}

            {!isLoading && (!sortedData || sortedData.length === 0) && (
              <div className="py-5">
                <NoContent
                  title="No Results"
                  image={image}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}

            {!isLoading &&
              sortedData
                .sort((a: any, b: any) => {
                  return a.groupedValue.toLowerCase() <
                    b.groupedValue.toLowerCase()
                    ? -1
                    : 1;
                })
                .map(({ groupedValue, records }) => {
                  return (
                    <>
                      {filters.groupByDate && (
                        <span className="title-table mt-3">
                          Out on {nameDayCompleteDateFormat(groupedValue)}
                        </span>
                      )}
                      {!filters.groupByDate && (
                        <span className="title-table mt-3">
                          Out days for {groupedValue}
                        </span>
                      )}
                      <table
                        className="table grey-table-header-background time-confirmation mb-4"
                        style={{ width: "100%" }}
                      >
                        <thead className="align-middle sticky-header sticky">
                          <tr>
                            <th
                              className={`border-left border-right border-top fw-500 py-3 ps-4 pointer column-20 ${
                                sortBy.orderBy === "person"
                                  ? "sorted-" + sortBy.criteria
                                  : ""
                              }`}
                              key="person"
                              id="Person"
                              onClick={() => handleSort("person", null)}
                            >
                              Person
                            </th>
                            <th
                              className={`border-top border-right fw-500 py-3 ps-4 pointer column-20 ${
                                sortBy.orderBy === "costCenter"
                                  ? "sorted-" + sortBy.criteria
                                  : ""
                              }`}
                              key="costCenter"
                              id="CostCenter"
                              onClick={() => handleSort("costCenter", null)}
                            >
                              Cost Center
                            </th>
                            <th
                              className={`border-top border-right fw-500 py-3 ps-4 pointer column-20 ${
                                sortBy.orderBy === "date"
                                  ? "sorted-" + sortBy.criteria
                                  : ""
                              }`}
                              key="date"
                              id="Date"
                              onClick={() => handleSort("date", null)}
                            >
                              Date
                            </th>
                            <th
                              className={`border-top border-right fw-500 py-3 ps-4 pointer column-20 ${
                                sortBy.orderBy === "hours"
                                  ? "sorted-" + sortBy.criteria
                                  : ""
                              }`}
                              key="hours"
                              id="Hours"
                              onClick={() => handleSort("hours", null)}
                            >
                              Hours
                            </th>
                            <th
                              className={`border-top border-right fw-500 py-3 ps-4 pointer column-20 ${
                                sortBy.orderBy === "description"
                                  ? "sorted-" + sortBy.criteria
                                  : ""
                              }`}
                              key="description"
                              id="Description"
                              onClick={() => handleSort("description", null)}
                            >
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((record: WhoIsOutUser, index) => (
                            <tr
                              key={newUuid()}
                              style={{ verticalAlign: "middle" }}
                              className={
                                index % 2 === 0 ? "even-row" : "odd-row"
                              }
                            >
                              <td className="py-3 ps-4 fw-500">
                                <div className="td-text">{record.person}</div>
                              </td>
                              <td className="py-3 ps-4 fw-500">
                                <div className="td-text">
                                  {record.costCenter.number} -{" "}
                                  {record.costCenter.name}
                                </div>
                              </td>
                              <td className="py-3 ps-4 fw-500">
                                <div className="td-text">
                                  {record.dateString}
                                </div>
                              </td>
                              <td className="py-3 ps-4 fw-500">
                                <div className="td-text">
                                  {quantityFormat(record.hours)}
                                </div>
                              </td>
                              <td className="py-3 ps-4 fw-500">
                                {decodeURIComponentSafe(record.description)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  );
                })}

            <Footer />
          </div>
        )}
    </>
  );
};
